import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronUpIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { shopApi, shopImageUrl } from '../../../utils/shopApi';
import { clearShopProductsCache } from '../shop/useShopProducts';
import { PageHeader, Skeleton, ErrorState, useConfirm, useToast, useUnsavedChanges, DISCARD } from '../../../components/ui';
import { PRODUCTS_PATH, errorOf } from './AdminShopProducts';
import '../booking-shared.css';
import './AdminShopProducts.css';

const emptySize = () => ({ key: Math.random(), label: '', price: '', stock: '', isActive: true });
const Optional = () => <span className="bk-optional"> (optional)</span>;

// Admin, Shop products: one product's page (/new for a new one)
export default function AdminShopProduct() {
  const { id } = useParams();
  const isNew = !id;
  const [initial, setInitial] = useState(isNew ? {} : null);
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(() => {
    if (isNew) { setInitial({}); return; }
    setLoadError(null);
    setInitial(null);
    shopApi.getAdminProducts()
      .then(res => {
        const found = res.data.find(p => p.id === id);
        if (found) setInitial(found);
        else setLoadError('missing');
      })
      .catch((err) => { console.error('Could not load the product', err); setLoadError('failed'); });
  }, [id, isNew]);
  useEffect(() => { load(); }, [load]);

  const back = { to: PRODUCTS_PATH, label: 'Shop products' };
  if (loadError || !initial) {
    return (
      <div className="bk-page bk-page--narrow">
        <PageHeader back={back} title="Product" />
        {loadError === 'missing' && <ErrorState title="Couldn't find that product" text="It may have been deleted." />}
        {loadError === 'failed' && <ErrorState title="Couldn't load the product" onRetry={load} />}
        {!loadError && <Skeleton rows={5} label="Loading the product…" />}
      </div>
    );
  }
  return <ProductEditor key={initial.id || 'new'} initial={initial} />;
}

// What's on the form, to tell whether anything has changed
const snapshot = (fields) => JSON.stringify(fields);

function ProductEditor({ initial }) {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const toast = useToast();
  const isNew = !initial.id;
  const [product, setProduct] = useState(initial);
  const [name, setName] = useState(initial.name || '');
  const [description, setDescription] = useState(initial.description || '');
  const [supplier, setSupplier] = useState(initial.supplier || '');
  const [isActive, setIsActive] = useState(initial.isActive ?? true);
  const [custom, setCustom] = useState(Boolean(initial.customisation));
  const [customLabel, setCustomLabel] = useState(initial.customisation?.label || 'Initials');
  const [customMax, setCustomMax] = useState(initial.customisation?.maxLength || 4);
  const [customRequired, setCustomRequired] = useState(initial.customisation?.required ?? true);
  const [sizeNote, setSizeNote] = useState(initial.sizeGuideNote || '');
  const [images, setImages] = useState(initial.images || []);
  const [guideImage, setGuideImage] = useState(initial.sizeGuideImage || null);
  const [sizes, setSizes] = useState(() => (initial.variants?.length
    ? initial.variants.map(v => ({ key: v.id, id: v.id, label: v.label, price: (v.price / 100).toFixed(2), stock: v.stock ?? '', isActive: v.isActive }))
    : [emptySize()]));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [nameError, setNameError] = useState(null);

  const fields = { name, description, supplier, isActive, custom, customLabel, customMax, customRequired, sizeNote, images, guideImage, sizes: sizes.map(({ key, ...s }) => s) };
  const [saved, setSaved] = useState(() => snapshot(fields));
  const changed = snapshot(fields) !== saved;
  useUnsavedChanges(changed && !busy);

  const setSize = (i, field, value) => setSizes(list => list.map((s, j) => (j === i ? { ...s, [field]: value } : s)));
  const moveSize = (i, by) => setSizes(list => {
    const next = [...list];
    const [s] = next.splice(i, 1);
    next.splice(i + by, 0, s);
    return next;
  });

  const payload = () => ({
    name: name.trim(),
    description: description.trim() || null,
    supplier: supplier.trim() || null,
    isActive,
    customisation: custom ? { label: customLabel.trim() || 'Initials', maxLength: Number(customMax) || 4, required: customRequired } : null,
    sizeGuideNote: sizeNote.trim() || null,
    ...(isNew ? {} : { images, sizeGuideImage: guideImage }),
    variants: sizes.filter(s => s.label.trim()).map(s => ({
      ...(s.id ? { id: s.id } : {}),
      label: s.label.trim(),
      price: Math.round(parseFloat(s.price || '0') * 100),
      stock: s.stock === '' || s.stock === null ? null : parseInt(s.stock, 10),
      isActive: s.isActive,
    })),
  });

  const leaveToList = () => {
    if (window.history.state?.idx > 0) navigate(-1);
    else navigate(PRODUCTS_PATH, { replace: true });
  };

  const save = async (e) => {
    e.preventDefault();
    const body = payload();
    setNameError(null);
    if (!body.name) {
      setNameError('Give the product a name');
      return setError('Give the product a name.');
    }
    if (!body.variants.length) return setError('Add at least one size (use "One size" if there is only one).');
    if (body.variants.some(v => Number.isNaN(v.price) || v.price < 0)) return setError('Every size needs a price.');
    if (body.variants.some(v => v.stock !== null && (Number.isNaN(v.stock) || v.stock < 0))) return setError('Stock must be a whole number, or blank to not count it.');
    setBusy(true);
    setError(null);
    try {
      const res = isNew ? await shopApi.createProduct(body) : await shopApi.saveProduct(product.id, body);
      clearShopProductsCache();
      setSaved(snapshot(fields));
      if (isNew) {
        // A new product stays open so photos can be added
        toast(`${body.name} saved. Add photos next.`);
        navigate(`${PRODUCTS_PATH}/${res.data.id}`, { replace: true });
      } else {
        toast(`${body.name} saved`);
        leaveToList();
      }
    } catch (err) {
      setError(errorOf(err, "Couldn't save. Try again."));
      setBusy(false);
    }
  };

  const upload = async (file, sizeGuide = false) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const res = await shopApi.uploadProductPhoto(product.id, file, { sizeGuide });
      setProduct(res.data);
      // An upload is saved straight away, so it isn't an unsaved change
      if (sizeGuide) {
        setGuideImage(res.data.sizeGuideImage);
        setSaved(s => snapshot({ ...JSON.parse(s), guideImage: res.data.sizeGuideImage }));
      } else {
        setImages(res.data.images);
        setSaved(s => snapshot({ ...JSON.parse(s), images: res.data.images }));
      }
      clearShopProductsCache();
      toast(sizeGuide ? 'Size chart added' : 'Photo added');
    } catch (err) {
      setError(errorOf(err, "Couldn't upload the photo. Try again."));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    const ok = await confirm({
      title: `Delete ${product.name}?`,
      message: 'It leaves the shop for good. Past orders keep their details.',
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await shopApi.deleteProduct(product.id);
      clearShopProductsCache();
      toast(`${product.name} deleted`);
      navigate(PRODUCTS_PATH, { replace: true });
    } catch (err) {
      setError(errorOf(err, "Couldn't delete. Try again."));
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (changed && !(await confirm(DISCARD))) return;
    leaveToList();
  };

  const moveImage = (i, by) => setImages(list => {
    const next = [...list];
    const [img] = next.splice(i, 1);
    next.splice(i + by, 0, img);
    return next;
  });

  return (
    <div className="bk-page bk-page--narrow">
      <PageHeader
        back={{ to: PRODUCTS_PATH, label: 'Shop products' }}
        title={isNew ? 'Add a product' : product.name}
        details={isNew ? 'Save the product first, then add photos.' : undefined}
      />
      <form className="sp-editor" onSubmit={save} noValidate>
        <label className="bk-label">
          Name
          <input className="bk-input" value={name} onChange={e => setName(e.target.value)} placeholder="For example, club hoodie"
            aria-invalid={nameError ? 'true' : undefined} />
          {nameError && <span className="bk-field-error">{nameError}</span>}
        </label>

        <label className="bk-label">
          Description<Optional />
          <textarea className="bk-input" rows={4} value={description} onChange={e => setDescription(e.target.value)}
            placeholder="For example, the fabric, the fit and what's printed on it" />
        </label>

        {!isNew && (
          <fieldset className="sp-box">
            <legend>Photos</legend>
            <div className="sp-photos">
              {images.map((url, i) => (
                <div key={url} className="sp-photo">
                  <img src={shopImageUrl(url)} alt={`Photo ${i + 1}`} />
                  <div className="sp-photo__tools">
                    <button type="button" className="ui-icon-btn" aria-label="Move photo left" disabled={i === 0} onClick={() => moveImage(i, -1)}>
                      <ChevronLeftIcon aria-hidden="true" />
                    </button>
                    <button type="button" className="ui-icon-btn" aria-label="Remove photo" onClick={() => setImages(list => list.filter(u => u !== url))}>
                      <XMarkIcon aria-hidden="true" />
                    </button>
                    <button type="button" className="ui-icon-btn" aria-label="Move photo right" disabled={i === images.length - 1} onClick={() => moveImage(i, 1)}>
                      <ChevronRightIcon aria-hidden="true" />
                    </button>
                  </div>
                </div>
              ))}
              <label className="sp-photo sp-photo--add">
                <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" hidden disabled={busy} onChange={e => { upload(e.target.files[0]); e.target.value = ''; }} />
                Add photo
              </label>
            </div>
            <small className="bk-small">The first photo is the one shown in the shop. Removing a photo takes effect when you save.</small>
          </fieldset>
        )}

        <fieldset className="sp-box">
          <legend>Sizes and prices</legend>
          <div className="sp-sizes">
            <div className="sp-size sp-size--head" aria-hidden="true">
              <span>Size</span><span>Price (£)</span><span>Stock</span><span>On sale</span><span />
            </div>
            {sizes.map((s, i) => (
              <div key={s.key} className="sp-size">
                <input className="bk-input" aria-label={`Size ${i + 1}`} value={s.label} onChange={e => setSize(i, 'label', e.target.value)} placeholder="For example, adult M" />
                <input className="bk-input" aria-label={`Price for size ${i + 1}`} inputMode="decimal" value={s.price} onChange={e => setSize(i, 'price', e.target.value)} placeholder="20.00" />
                <input className="bk-input" aria-label={`Stock for size ${i + 1}`} inputMode="numeric" value={s.stock} onChange={e => setSize(i, 'stock', e.target.value.replace(/[^0-9]/g, ''))} placeholder="Not counted" />
                <input type="checkbox" className="bk-checkbox" aria-label={`Size ${i + 1} on sale`} checked={s.isActive} onChange={e => setSize(i, 'isActive', e.target.checked)} />
                <span className="sp-size__tools">
                  <button type="button" className="ui-icon-btn" aria-label="Move size up" disabled={i === 0} onClick={() => moveSize(i, -1)}>
                    <ChevronUpIcon aria-hidden="true" />
                  </button>
                  <button type="button" className="ui-icon-btn" aria-label="Move size down" disabled={i === sizes.length - 1} onClick={() => moveSize(i, 1)}>
                    <ChevronDownIcon aria-hidden="true" />
                  </button>
                  <button type="button" className="ui-icon-btn" aria-label="Remove size" disabled={sizes.length === 1} onClick={() => setSizes(list => list.filter((_, j) => j !== i))}>
                    <XMarkIcon aria-hidden="true" />
                  </button>
                </span>
              </div>
            ))}
          </div>
          <button type="button" className="bk-btn bk-btn--sm sp-add-size" onClick={() => setSizes(list => [...list, emptySize()])}>Add a size</button>
          <small className="bk-small">Leave stock blank to sell without limit. With a number, the shop shows how many are left, stops at sold out, and takes stock off when an order is paid.</small>
        </fieldset>

        <fieldset className="sp-box">
          <legend>Personalising</legend>
          <label className="sp-check">
            <input type="checkbox" className="bk-checkbox" checked={custom} onChange={e => setCustom(e.target.checked)} />
            Buyers add initials or a name
          </label>
          {custom && (
            <div className="sp-row">
              <label className="bk-label">
                Called
                <input className="bk-input" value={customLabel} onChange={e => setCustomLabel(e.target.value)} />
              </label>
              <label className="bk-label">
                Up to (letters)
                <input className="bk-input" inputMode="numeric" value={customMax} onChange={e => setCustomMax(e.target.value.replace(/[^0-9]/g, ''))} />
              </label>
              <label className="sp-check">
                <input type="checkbox" className="bk-checkbox" checked={customRequired} onChange={e => setCustomRequired(e.target.checked)} />
                Must be filled in
              </label>
            </div>
          )}
        </fieldset>

        <fieldset className="sp-box">
          <legend>Size guide</legend>
          {product.sizeGuide && <p className="bk-small">This product also has a size chart that came from the old shop; it still shows.</p>}
          <label className="bk-label">
            Note<Optional />
            <textarea className="bk-input" rows={2} value={sizeNote} onChange={e => setSizeNote(e.target.value)} placeholder="For example, comes up small, order a size up" />
          </label>
          {!isNew && (
            <div className="sp-guide">
              {guideImage && (
                <div className="sp-photo">
                  <img src={shopImageUrl(guideImage)} alt="Size guide" />
                  <div className="sp-photo__tools">
                    <button type="button" className="ui-icon-btn" aria-label="Remove size guide picture" onClick={() => setGuideImage(null)}>
                      <XMarkIcon aria-hidden="true" />
                    </button>
                  </div>
                </div>
              )}
              <label className="bk-btn bk-btn--sm">
                <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" hidden disabled={busy} onChange={e => { upload(e.target.files[0], true); e.target.value = ''; }} />
                {guideImage ? 'Replace size chart' : 'Add a size chart'}
              </label>
            </div>
          )}
        </fieldset>

        <label className="bk-label">
          Supplier<Optional />
          <input className="bk-input" value={supplier} onChange={e => setSupplier(e.target.value)} />
          <span className="bk-small">Filled in on new orders for this product.</span>
        </label>

        <label className="sp-check sp-shown">
          <input type="checkbox" className="bk-checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} />
          Show in the shop
        </label>

        {error && <p className="bk-form-error" role="alert">{error}</p>}
        <div className="bk-actions">
          <button type="submit" className="bk-btn bk-btn--primary" disabled={busy}>{busy ? 'Saving…' : isNew ? 'Save and add photos' : 'Save'}</button>
          <button type="button" className="bk-btn" onClick={cancel} disabled={busy}>Cancel</button>
        </div>
        {!isNew && (
          <div className="bk-actions">
            <button type="button" className="bk-btn bk-btn--outline-danger" onClick={remove} disabled={busy}>Delete product</button>
          </div>
        )}
      </form>
    </div>
  );
}
